var m = require('mithril');
var treeDevice = require('../../network/components/deviceTree');
var button = require('../../components/button');
var ALLOWED_DEVICE_CLASSES = {
  bacnetdevice: true,
  modbusdevice: true,
  lontalkdevice: true,
  restresource: true
};

function toTrimmedString(value) {
  if (value === undefined || value === null) return '';
  return String(value).trim();
}

function normalizeRid(value) {
  var rid = toTrimmedString(value);
  if (!rid) return '';
  return rid.charAt(0) === '#' ? rid : ('#' + rid);
}

function isValidRid(value) {
  return /^#-?\d+:\d+$/.test(toTrimmedString(value));
}

function ridToRequestId(value) {
  var rid = normalizeRid(value);
  return rid ? rid.slice(1) : '';
}

function parseDeviceIdList(value) {
  return toTrimmedString(value)
    .split(/[,\n]/)
    .map(function (token) { return token.trim(); })
    .filter(Boolean);
}

function normalizeTemplateDevice(entry, index) {
  if (entry === undefined || entry === null) return null;
  var normalized = (entry && typeof entry === 'object' && !Array.isArray(entry)) ? entry : {};

  var name = toTrimmedString(
    normalized.name ||
    normalized.device ||
    normalized.label ||
    (typeof entry === 'string' ? entry : '')
  );

  var rid = toTrimmedString(
    normalized.id ||
    normalized.deviceId ||
    normalized.rid ||
    normalized['@rid']
  );

  var displayName = name || rid || ('Device ' + (index + 1));
  var keyBase = rid || name || ('device-' + index);
  var path = toTrimmedString(
    normalized.path ||
    normalized.devicePath ||
    normalized.fullPath
  );

  return {
    key: 'required-device-' + index + '-' + keyBase,
    name: name,
    rid: rid,
    path: path,
    displayName: displayName
  };
}

function parseRequiredDevicesFromTemplateText(templateText) {
  var raw = toTrimmedString(templateText);
  if (!raw) {
    return { devices: [], error: '' };
  }

  try {
    var parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { devices: [], error: '' };
    }
    var entries = Array.isArray(parsed.devices) ? parsed.devices : [];
    return {
      devices: entries.map(normalizeTemplateDevice).filter(Boolean),
      error: ''
    };
  } catch (error) {
    return {
      devices: [],
      error: 'Template JSON is invalid. Fix JSON errors to map required devices automatically.'
    };
  }
}

function buildSelectionList(requiredDevices, targetDeviceIds, previousSelections, options) {
  var opts = options && typeof options === 'object' ? options : {};
  var preserveCustomRows = opts.preserveCustomRows === true;
  var required = Array.isArray(requiredDevices) ? requiredDevices : [];
  var previous = Array.isArray(previousSelections) ? previousSelections : [];
  var normalizedTargetValue = parseDeviceIdList(targetDeviceIds).join(', ');
  var ridTokens = normalizedTargetValue ? normalizedTargetValue.split(', ') : [];
  var prevByKey = {};
  previous.forEach(function (selection) {
    if (!selection || !selection.templateKey) return;
    prevByKey[selection.templateKey] = selection;
  });

  var buildRequiredSelections = function () {
    return required.map(function (device, index) {
      var rid = ridTokens[index] || '';
      var prior = prevByKey[device.key] || previous[index] || null;
      var name = prior && prior.rid === rid ? toTrimmedString(prior.name) : '';
      return {
        templateKey: device.key,
        rid: rid,
        name: name,
        path: prior && prior.rid === rid ? toTrimmedString(prior.path) : ''
      };
    });
  };

  if (previous.length) {
    var normalizedPreviousValue = composeTargetDeviceIds(previous);
    if (normalizedPreviousValue === normalizedTargetValue) {
      if (!required.length && (preserveCustomRows || previous.length === required.length)) {
        return previous.map(function (selection, index) {
          var device = required[index] || {};
          return {
            templateKey: toTrimmedString(selection && selection.templateKey) || toTrimmedString(device.key) || ('device-row-' + index),
            rid: toTrimmedString(selection && selection.rid),
            name: toTrimmedString(selection && selection.name),
            path: toTrimmedString(selection && selection.path)
          };
        });
      }
      if (required.length && preserveCustomRows) {
        var requiredSelections = buildRequiredSelections();
        var requiredKeys = {};
        required.forEach(function (device) {
          if (device && device.key) {
            requiredKeys[device.key] = true;
          }
        });
        var customRows = previous
          .filter(function (selection) {
            var key = toTrimmedString(selection && selection.templateKey);
            return key && !requiredKeys[key];
          })
          .map(function (selection, index) {
            return {
              templateKey: toTrimmedString(selection && selection.templateKey) || ('custom-device-' + index),
              rid: toTrimmedString(selection && selection.rid),
              name: toTrimmedString(selection && selection.name),
              path: toTrimmedString(selection && selection.path)
            };
          });
        return requiredSelections.concat(customRows);
      }
    }
  }

  if (!required.length) {
    return ridTokens.map(function (rid, index) {
      var prior = previous[index] || null;
      return {
        templateKey: toTrimmedString(prior && prior.templateKey) || ('device-row-' + index),
        rid: rid,
        name: prior && prior.rid === rid ? toTrimmedString(prior.name) : '',
        path: prior && prior.rid === rid ? toTrimmedString(prior.path) : ''
      };
    });
  }

  return buildRequiredSelections();
}

function composeTargetDeviceIds(selectionList) {
  var selections = Array.isArray(selectionList) ? selectionList : [];
  return selections
    .map(function (selection) { return toTrimmedString(selection && selection.rid); })
    .filter(Boolean)
    .join(', ');
}

function buildPathString(rawPath) {
  if (!rawPath) return '';
  if (typeof rawPath === 'string') return toTrimmedString(rawPath);
  if (Array.isArray(rawPath)) {
    return rawPath
      .map(function (node) {
        if (!node) return '';
        if (typeof node === 'string') return toTrimmedString(node);
        return toTrimmedString(node.name || node.label || node['@class'] || node['@rid']);
      })
      .filter(Boolean)
      .join(' / ');
  }
  return '';
}

function isSelectableDeviceClass(className) {
  var cls = toTrimmedString(className).toLowerCase();
  if (!cls) return false;
  return ALLOWED_DEVICE_CLASSES[cls] === true;
}

function toResolvedDeviceRecord(record, fallbackRid) {
  if (!record || typeof record !== 'object') return null;
  var rid = normalizeRid(record['@rid'] || fallbackRid);
  if (!isValidRid(rid)) return null;
  if (!isSelectableDeviceClass(record['@class'])) return null;
  var name = toTrimmedString(record.name || record.label);
  var path = buildPathString(record.path || record.$path || record.breadcrumbs);
  return {
    rid: rid,
    name: name,
    path: path
  };
}

function resolveDeviceByRid(value) {
  var rid = normalizeRid(value);
  if (!isValidRid(rid)) return Promise.resolve(null);
  var ridId = ridToRequestId(rid);
  if (!ridId) return Promise.resolve(null);

  return m.request({
    method: 'GET',
    url: '/api/v1/binding/device/' + encodeURIComponent(ridId) + '/resolve'
  })
    .then(function (record) {
      return toResolvedDeviceRecord(record, rid);
    })
    .catch(function () {
      return null;
    });
}

function resolveDeviceByRidWithError(value) {
  var rid = normalizeRid(value);
  if (!rid) {
    return Promise.resolve({ device: null, error: '' });
  }
  if (!isValidRid(rid)) {
    return Promise.resolve({
      device: null,
      error: 'Invalid RID format. Use #12:0.'
    });
  }
  return resolveDeviceByRid(rid)
    .then(function (device) {
      if (!device) {
        return {
          device: null,
          error: 'RID not found or it is not a supported device.'
        };
      }
      return { device: device, error: '' };
    })
    .catch(function () {
      return {
        device: null,
        error: 'Unable to validate device RID right now.'
      };
    });
}

function buildTargetDeviceHelpText(requiredDeviceCount) {
  var count = Number(requiredDeviceCount) || 0;
  if (count > 0) {
    return 'The template requires ' + count + ' device' + (count === 1 ? '' : 's') + '. Type a target device ID or use the search icon to browse. Devices are applied in order.';
  }
  return 'Type a target device ID or use the search icon to browse. Devices are applied in order.';
}

function renderRequiredDeviceSelector(options) {
  var opts = options && typeof options === 'object' ? options : {};
  var requiredDevices = Array.isArray(opts.requiredDevices) ? opts.requiredDevices : [];

  var selections = Array.isArray(opts.selections) ? opts.selections : [];
  var allowAddRemoveRows = opts.allowAddRemoveRows === true;
  if (!allowAddRemoveRows && !requiredDevices.length && !selections.length) return null;

  var rows = allowAddRemoveRows
    ? selections.map(function (selection, index) {
        var device = requiredDevices[index] || {};
        return {
          key: toTrimmedString(selection && selection.templateKey) || device.key || ('device-row-' + index),
          selection: selection || {},
          templateDevice: device
        };
      })
    : requiredDevices.map(function (device, index) {
        return {
          key: device.key || ('required-device-' + index),
          selection: selections[index] || {},
          templateDevice: device
        };
      });
  var expandedIndex = typeof opts.expandedIndex === 'number' ? opts.expandedIndex : -1;
  var selectedNode = typeof opts.selectedNode === 'function' ? opts.selectedNode : null;
  var disabled = opts.disabled === true;
  var onToggleExpand = typeof opts.onToggleExpand === 'function' ? opts.onToggleExpand : function () {};
  var onSelect = typeof opts.onSelect === 'function' ? opts.onSelect : function () {};
  var onClear = typeof opts.onClear === 'function' ? opts.onClear : function () {};
  var onDeleteRow = typeof opts.onDeleteRow === 'function' ? opts.onDeleteRow : function () {};
  var onAddRow = typeof opts.onAddRow === 'function' ? opts.onAddRow : function () {};
  var onChangeRid = typeof opts.onChangeRid === 'function' ? opts.onChangeRid : function () {};
  var onBlurRid = typeof opts.onBlurRid === 'function' ? opts.onBlurRid : function () {};
  var sectionLabel = Object.prototype.hasOwnProperty.call(opts, 'sectionLabel')
    ? opts.sectionLabel
    : 'Template Required Devices';
  var sectionHint = opts.sectionHint || 'Type a target device RID or use the search icon to browse.';
  var rowCountLabel = Object.prototype.hasOwnProperty.call(opts, 'rowCountLabel')
    ? opts.rowCountLabel
    : (allowAddRemoveRows
      ? ('Template suggests ' + requiredDevices.length + ' row(s). You can add or remove rows as needed.')
      : ('Showing ' + requiredDevices.length + ' row(s), one per required device reference in the selected template.'));

  return m('div.mb2', [
    sectionLabel ? m('div.f6.b.mb1', sectionLabel) : null,
    rowCountLabel ? m('div.f6.gray.mb1', rowCountLabel) : null,
    m('div.f6.gray.mb2', sectionHint),
    m('div.flex.items-center.mv1', [
      m('div.pr2.f6.b.near-white-50.ttc', { style: { flex: '0 0 20%', maxWidth: '20%' } }, 'Name'),
      m('div.pr2.f6.b.near-white-50.ttc', { style: { flex: '0 0 15%', maxWidth: '15%' } }, 'Device ID'),
      m('div.pr2.f6.b.near-white-50.ttc', { style: { flex: '0 0 45%', maxWidth: '45%' } }, 'Path'),
      m('div.f6.b.near-white-50.ttc', { style: { flex: '0 0 20%', maxWidth: '20%' } }, 'Actions')
    ]),
    rows.map(function (row, index) {
      var selection = row.selection || {};
      var isExpanded = expandedIndex === index;
      return m('div.mv1', { key: row.key || ('required-device-' + index) }, [
        m('div.flex.items-center', [
          m('div.pr2', { style: { flex: '0 0 20%', maxWidth: '20%' } }, [
            m('div.f6.near-white', toTrimmedString(selection.name) || '-')
          ]),
          m('div.pr2', { style: { flex: '0 0 15%', maxWidth: '15%' } }, [
            m('input.form-control.w-100', {
              type: 'text',
              value: toTrimmedString(selection.rid),
              disabled: disabled,
              oninput: function (event) {
                onChangeRid(index, event && event.target ? event.target.value : '');
              },
              onblur: function (event) {
                onBlurRid(index, event && event.target ? event.target.value : '');
              }
            })
          ]),
          m('div.pr2', { style: { flex: '0 0 45%', maxWidth: '45%' } }, [
            m('div.f6.near-white', toTrimmedString(selection.path) || '-')
          ]),
          m('div', { style: { flex: '0 0 20%', maxWidth: '20%' } }, [
            m('div.flex.items-center', [
              m(button, {
                label: m('i.f4.ion-search.blue'),
                class: 'w-50 mr1 ' + (isExpanded ? 'bg-mid-gray near-white' : '') + (disabled ? ' o-50' : ''),
                func: function () {
                  if (disabled) return;
                  onToggleExpand(index);
                }
              }),
              m(button, {
                label: m('i.f4.ion-ios-trash.red'),
                class: 'w-50 ' + (disabled ? 'o-50' : ''),
                func: function () {
                  if (disabled) return;
                  if (allowAddRemoveRows) {
                    onDeleteRow(index);
                  } else {
                    onClear(index);
                  }
                }
              })
            ])
          ])
        ]),
        isExpanded
          ? m('div.mt2.ba.b--moon-gray.br2.pa2', [
              m(treeDevice, {
                selectedNode: selectedNode,
                loader: 'background',
                itemsPerPage: 15
              }),
              m('div.mt3.tr', [
                m(button, {
                  label: 'Use Selected Device',
                  class: 'btn-good dib ph3 pv2 ' + ((selectedNode && selectedNode()) ? '' : 'o-50') + (disabled ? ' o-50' : ''),
                  func: function () {
                    if (disabled || !selectedNode) return;
                    var selectedDevice = selectedNode();
                    if (!selectedDevice || !selectedDevice['@rid']) return;
                    onSelect(index, selectedDevice);
                  },
                })
              ])
            ])
          : null,
        toTrimmedString(selection.error)
          ? m('div.f7.red.mt1', toTrimmedString(selection.error))
          : null
      ]);
    }),
    allowAddRemoveRows ? m('div.flex.items-center.mt2', [
      m('div', { style: { flex: '0 0 80%', maxWidth: '80%' } }),
      m('div', { style: { flex: '0 0 20%', maxWidth: '20%' } }, [
        m(button, {
          label: m('i.f4.ion-plus.blue'),
          class: 'w-50 fr' + (disabled ? ' o-50' : ''),
          func: function () {
            if (disabled) return;
            onAddRow();
          }
        })
      ])
    ]) : null
  ]);
}

module.exports = {
  normalizeRid: normalizeRid,
  isValidRid: isValidRid,
  parseDeviceIdList: parseDeviceIdList,
  parseRequiredDevicesFromTemplateText: parseRequiredDevicesFromTemplateText,
  buildSelectionList: buildSelectionList,
  composeTargetDeviceIds: composeTargetDeviceIds,
  buildTargetDeviceHelpText: buildTargetDeviceHelpText,
  resolveDeviceByRid: resolveDeviceByRid,
  resolveDeviceByRidWithError: resolveDeviceByRidWithError,
  renderRequiredDeviceSelector: renderRequiredDeviceSelector
};
